import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useUserContext } from '../components/Providers/UserContext';
import {
  apiGetUserList,
  getPollsData,
  getSessionsDailyReport,
  getTutorRep,
  getUserFeedbacksData,
  getVendorUpcomingClass,
} from '../utils/Network';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { formatDateToCompact } from '../utils/dateUtils';

// Locales supported by this app. Import more locales when needed.
import 'dayjs/locale/en';      // US, Canada, default English
import 'dayjs/locale/en-gb';  // Great Britain
import 'dayjs/locale/es';     // Spanish (Latin America)
import 'dayjs/locale/pt-br';  // Portuguese (Brazil)
import 'dayjs/locale/fr-ca';  // French (Canada)
import 'dayjs/locale/nl';     // Dutch (Suriname, Caribbean)

const supportedLocales = [
  'en',     // US, Canada, default
  'en-gb',  // Great Britain
  'es',     // Spanish
  'pt-br',  // Portuguese (Brazil)
  'fr-ca',  // French (Canada)
  'nl',     // Dutch
];

function getSupportedLocale(lang: string) {
  const lower = lang.toLowerCase();
  if (supportedLocales.includes(lower)) return lower;
  if (supportedLocales.includes(lower.split('-')[0])) return lower.split('-')[0];
  // Map some common browser locales to Dayjs locales
  if (lower.startsWith('en-')) return lower === 'en-gb' ? 'en-gb' : 'en';
  if (lower.startsWith('es-')) return 'es';
  if (lower.startsWith('pt-')) return 'pt-br';
  if (lower.startsWith('fr-')) return lower === 'fr-ca' ? 'fr-ca' : 'fr';
  if (lower.startsWith('nl-')) return 'nl';
  return 'en'; // fallback
}

const userLocale = getSupportedLocale(navigator.language);
dayjs.locale(userLocale);

const Analytics = () => {
  // const formatDate = (date: Date) => date.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());


  function downloadTextAsFile(blob: Blob, filename: string) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;

    // Append the anchor element to the body
    document.body.appendChild(a);

    // Programmatically trigger a click event on the anchor element
    a.click();

    // Remove the temporary anchor element from the document
    document.body.removeChild(a);
  }

  const { userToken } = useUserContext();

  const returnCsvFilename = (from: string, to: string, prefix?: string): string => {
    const date: string = `${formatDateToCompact(from)}_${formatDateToCompact(to)}.csv`

    if (prefix) return `${prefix}_${date}`;
    return `${date}`;
  }

  const getUserList = async (fromDate: Dayjs, toDate: Dayjs) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const userList = await apiGetUserList(from, to, userToken);
    downloadTextAsFile(userList, returnCsvFilename(from, to, 'user_list'));
  };

  const getUserPolls = async (fromDate: Dayjs, toDate: Dayjs) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const vipUpcoming = await getPollsData(from, to, userToken);
    downloadTextAsFile(vipUpcoming, returnCsvFilename(from, to, 'user_polls'));
  };

  const getUserFeedbacks = async (fromDate: Dayjs, toDate: Dayjs) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const userFeedbacks = await getUserFeedbacksData(from, to, userToken);
    downloadTextAsFile(userFeedbacks, returnCsvFilename(from, to, 'user_feedbacks'));
  };

  const getDailyReport = async (fromDate: Dayjs, toDate: Dayjs) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const dailyReport = await getSessionsDailyReport(from, to, userToken);
    downloadTextAsFile(dailyReport, returnCsvFilename(from, to, 'daily_report'));
  };

  const getUpcomingClass = async (fromDate: Dayjs, toDate: Dayjs, vendor: string) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const upcomingClass = await getVendorUpcomingClass(from, to, vendor, userToken);
    downloadTextAsFile(upcomingClass, returnCsvFilename(from, to, 'upcoming_class'));
  };

  const getTutorReport = async (fromDate: Dayjs, toDate: Dayjs, vendor: string) => {
    const from = fromDate.format('YYYY-MM-DD');
    const to = toDate.format('YYYY-MM-DD');
    const upcomingClass = await getTutorRep(from, to, vendor, userToken);
    downloadTextAsFile(upcomingClass, returnCsvFilename(from, to, 'tutor_report'));
  };

  return (
    <>
      <div style={{ justifyContent: 'center', margin: '50px 150px' }}>
        <div style={{ marginBottom: '30px', display: 'flex', gap: '20px' }}>
          <LocalizationProvider 
            dateAdapter={AdapterDayjs}
            adapterLocale={userLocale} // Use the detected locale
          >
            <DatePicker
              label="Start Date"
              defaultValue={dayjs()}
              value={startDate}
              maxDate={endDate}
              onChange={(newDate) => {
                if (newDate) setStartDate(newDate);
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: { width: 170 }
                }
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider 
            dateAdapter={AdapterDayjs}
            adapterLocale={userLocale} // Use the detected locale
          >
            <DatePicker
              label="End Date"
              defaultValue={dayjs()}
              value={endDate}
              minDate={startDate}
              onChange={(newDate) => {
                if (newDate) setEndDate(newDate);
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  sx: { width: 170 }
                }
              }}
            />
          </LocalizationProvider>
        </div>

        <div style={{ display: 'flex', gap: '40px', flexDirection: 'column' }}>
          <div
            onClick={() => getUserList(startDate, endDate)}
            style={{
              width: 150,
              height: 50,
              borderRadius: 12,
              padding: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexFlow: 'wrap',
              backgroundColor: '#a9dbd2',
              cursor: 'pointer',
            }}
          >
            Get User List
          </div>
          <div
            onClick={() => getUserPolls(startDate, endDate)}
            style={{
              width: 150,
              height: 50,
              borderRadius: 12,
              padding: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexFlow: 'wrap',
              backgroundColor: '#F44336',
              cursor: 'pointer',
            }}
          >
            User Polls
          </div>
          <div
            onClick={() => getUserFeedbacks(startDate, endDate)}
            style={{
              width: 150,
              height: 50,
              borderRadius: 12,
              padding: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexFlow: 'wrap',
              backgroundColor: '#d7b98f',
              cursor: 'pointer',
            }}
          >
            User Feedbacks
          </div>
          <div
            onClick={() => getDailyReport(startDate, endDate)}
            style={{
              width: 150,
              height: 50,
              borderRadius: 12,
              padding: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexFlow: 'wrap',
              backgroundColor: '#2c97ca',
              cursor: 'pointer',
            }}
          >
            Daily Report
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: '30px' }}
          >
            <input
              style={{
                width: 200,
                height: 50,
                padding: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexFlow: 'wrap',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                border: 'none',
                borderBottom: '1px solid #000',
              }}
              type="text"
              placeholder="Vendor Name"
              id="vendor"
            />
            <div
              onClick={() => {
                const vendor = (document.getElementById('vendor') as HTMLInputElement).value;
                getUpcomingClass(startDate, endDate, vendor);
              }}
              style={{
                width: 200,
                height: 50,
                borderRadius: 12,
                padding: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexFlow: 'wrap',
                backgroundColor: '#96d78f',
                cursor: 'pointer',
              }}
            >
              Get Upcoming Class
            </div>
            <div
              onClick={() => {
                const vendor = (document.getElementById('vendor') as HTMLInputElement).value;
                getTutorReport(startDate, endDate, vendor);
              }}
              style={{
                width: 200,
                height: 50,
                borderRadius: 12,
                padding: 10,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexFlow: 'wrap',
                backgroundColor: '#d38fd7',
                cursor: 'pointer',
              }}
            >
              Get Tutor Report
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default Analytics;
